import { useEffect } from "react";

import { type QueryKey, useInfiniteQuery } from "@tanstack/react-query";

import { useChatStore } from "@virtual-brain-lib/frontend-design-system/components";
import type { Chat, ChatMessage } from "@virtual-brain-lib/types";

import { normalizeProcessingMessage } from "./useChatMessagesCache";

export function useFetchChatMessages({
	chatId,
	queryKey,
	fetchMessages,
	pageSize,
}: {
	chatId: Chat["id"];
	queryKey: QueryKey;
	fetchMessages: (offset: number, limit: number) => Promise<ChatMessage[]>;
	pageSize?: number;
}) {
	const effectivePageSize = pageSize ?? 1000;
	const setAnsweringMessages = useChatStore((store) => store.setAnsweringMessages);

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
		isPlaceholderData,
		isError,
	} = useInfiniteQuery({
		queryKey,
		async queryFn({ pageParam }) {
			const messages = await fetchMessages(pageParam, effectivePageSize);

			return messages.map(normalizeProcessingMessage);
		},
		getNextPageParam(lastBatch, allBatches) {
			if (!pageSize) {
				return undefined;
			}

			if (lastBatch.length < effectivePageSize) {
				return undefined;
			}

			return allBatches.flat().length;
		},
		initialPageParam: 0,
		enabled: Boolean(chatId),
		staleTime: 0,
		gcTime: 0,
	});

	useEffect(() => {
		if (!data) {
			return;
		}

		setAnsweringMessages(
			data.pages
				.flat()
				.filter((message) => message.role === "assistant")
				.map((message) => ({
					messageId: message.id,
					isAnswering: message.status === "processing",
				})),
		);
	}, [data, setAnsweringMessages]);

	const fetchMoreMessages = async (): Promise<void> => {
		await fetchNextPage();
	};

	return {
		messages: data?.pages.toReversed().flat() ?? [],
		isLoading,
		isPlaceholderData,
		fetchMoreMessages,
		hasMoreMessages: hasNextPage ?? false,
		isFetchingMoreMessages: isFetchingNextPage,
		isError,
	};
}
